import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { AuthContext } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { Input } from './ui/input';
import { Progress } from './ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from './ui/accordion';
import { 
  User, Mail, Phone, Church, ArrowLeft, TrendingUp,
  BookOpen, CheckCircle, AlertTriangle,
  Save, StickyNote, BarChart3, Send, GraduationCap,
  Calendar, Award, Target, Plus, Pencil, Trash2,
  ClipboardList, FileText, Star, Download, Video,
  MessageSquare, Users
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine } from 'recharts';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const DetalleEstudiante = ({
  estudianteId: estudianteIdProp,
  claseId: claseIdProp,
  embedded = false,
  onClose,
} = {}) => {
  const { user, getImageUrl } = useContext(AuthContext);
  // Cuando se usa embebido (ej. dentro de SalaDocente > Avance), recibimos
  // los IDs via props para NO navegar fuera del componente padre — eso
  // desmontaría SalaDocente y cortaría la videollamada/reunión activa.
  // En modo standalone (acceso por URL directa), seguimos usando los
  // params/searchParams de react-router como siempre.
  const params = useParams();
  const [searchParams] = useSearchParams();
  const estudianteId = estudianteIdProp || params.estudianteId;
  const claseId = claseIdProp || searchParams.get('clase_id');
  const navigate = useNavigate();
  // Helper: en modo embebido el "volver" cierra el dialog; en standalone navega
  const handleBack = () => {
    if (embedded && onClose) onClose();
    else navigate(-1);
  };
  
  const [loading, setLoading] = useState(true);
  const [estudiante, setEstudiante] = useState(null);
  const [detalleClase, setDetalleClase] = useState(null);
  const [rendimientoGlobal, setRendimientoGlobal] = useState(null);
  
  const [historialCalificaciones, setHistorialCalificaciones] = useState([]);
  const [notaPrivada, setNotaPrivada] = useState('');
  // Modal envío de ficha por email
  const [showEnviarFichaModal, setShowEnviarFichaModal] = useState(false);
  const [enviarFichaForm, setEnviarFichaForm] = useState({
    destinatario_email: '',
    cc_input: '',
    mensaje_personal: '',
    incluir_calificaciones: true,
    incluir_asistencias: true,
  });
  const [enviandoFicha, setEnviandoFicha] = useState(false);
  const [notaOriginal, setNotaOriginal] = useState('');
  const [savingNota, setSavingNota] = useState(false);
  const [sendingAlert, setSendingAlert] = useState(false);
  
  const [calificacionesCompletas, setCalificacionesCompletas] = useState(null);
  
  const [modalCalificacionOpen, setModalCalificacionOpen] = useState(false);
  const [calificacionEditando, setCalificacionEditando] = useState(null);
  const [formCalificacion, setFormCalificacion] = useState({
    nombre: '',
    calificacion: '',
    descripcion: ''
  });
  const [savingCalificacion, setSavingCalificacion] = useState(false);
  const [deletingCalificacion, setDeletingCalificacion] = useState(null);
  
  const [esSoloLectura, setEsSoloLectura] = useState(false);
  const [exportandoPDF, setExportandoPDF] = useState(false);

  const [fichaRegistro, setFichaRegistro] = useState(null);
  const [loadingFicha, setLoadingFicha] = useState(false);

  // Notas compartidas
  const [notasCompartidas, setNotasCompartidas] = useState([]);
  const [loadingNotasCompartidas, setLoadingNotasCompartidas] = useState(false);
  const [nuevaNotaCompartida, setNuevaNotaCompartida] = useState('');
  const [savingNotaCompartida, setSavingNotaCompartida] = useState(false);

  useEffect(() => {
    if (estudianteId) {
      loadData();
    }
  }, [estudianteId, claseId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const headers = { 'Authorization': `Bearer ${localStorage.getItem('token')}` };
      
      const rendimientoResponse = await axios.get(
        `${API}/profesor/estudiante/${estudianteId}/rendimiento-global`, 
        { headers }
      );
      setRendimientoGlobal(rendimientoResponse.data);
      setEstudiante(rendimientoResponse.data.estudiante);
      
      if (claseId) {
        const [detalleResponse, historialResponse, calificacionesResponse] = await Promise.all([
          axios.get(`${API}/profesor/estudiante/${estudianteId}/detalle?clase_id=${claseId}`, { headers }),
          axios.get(`${API}/profesor/estudiante/${estudianteId}/historial-calificaciones?clase_id=${claseId}`, { headers }),
          axios.get(`${API}/profesor/estudiante/${estudianteId}/calificaciones-completas?clase_id=${claseId}`, { headers })
        ]);
        
        setDetalleClase(detalleResponse.data);
        setHistorialCalificaciones(historialResponse.data || []);
        setCalificacionesCompletas(calificacionesResponse.data);
        
        const soloLectura = detalleResponse.data?.es_solo_lectura || calificacionesResponse.data?.es_solo_lectura || false;
        setEsSoloLectura(soloLectura);
        
        // Load private notes only if not read-only
        if (!soloLectura) {
          try {
            const notasResponse = await axios.get(
              `${API}/profesor/notas-privadas?estudiante_id=${estudianteId}&clase_id=${claseId}`, 
              { headers }
            );
            setNotaPrivada(notasResponse.data?.nota || '');
            setNotaOriginal(notasResponse.data?.nota || '');
          } catch {
            setNotaPrivada('');
            setNotaOriginal('');
          }
        } else {
          setNotaPrivada('');
          setNotaOriginal('');
        }

        // Load shared notes (always)
        loadNotasCompartidas();
      }
      
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Error al cargar datos del estudiante');
    } finally {
      setLoading(false);
    }
  };

  const loadNotasCompartidas = async () => {
    if (!claseId) return;
    setLoadingNotasCompartidas(true);
    try {
      const response = await axios.get(
        `${API}/profesor/notas-compartidas?estudiante_id=${estudianteId}&clase_id=${claseId}`,
        { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }}
      );
      setNotasCompartidas(response.data || []);
    } catch (error) {
      console.error('Error loading notas compartidas:', error);
    } finally {
      setLoadingNotasCompartidas(false);
    }
  };

  const guardarNotaCompartida = async () => {
    if (!nuevaNotaCompartida.trim() || !claseId) return;
    setSavingNotaCompartida(true);
    try {
      await axios.post(`${API}/profesor/notas-compartidas`, {
        estudiante_id: estudianteId,
        clase_id: claseId,
        nota: nuevaNotaCompartida
      }, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      setNuevaNotaCompartida('');
      toast.success('Nota compartida guardada');
      loadNotasCompartidas();
    } catch (error) {
      console.error('Error saving shared note:', error);
      toast.error('Error al guardar nota compartida');
    } finally {
      setSavingNotaCompartida(false);
    }
  };

  const eliminarNotaCompartida = async (notaId) => {
    if (!window.confirm('¿Eliminar esta nota?')) return;
    try {
      await axios.delete(`${API}/profesor/notas-compartidas/${notaId}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      toast.success('Nota eliminada');
      loadNotasCompartidas();
    } catch (error) {
      console.error('Error deleting shared note:', error);
      toast.error('Error al eliminar nota');
    }
  };

  const loadFichaRegistro = async () => {
    setLoadingFicha(true);
    try {
      const response = await axios.get(
        `${API}/estudiante/${estudianteId}/ficha-registro`,
        { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }}
      );
      setFichaRegistro(response.data);
    } catch (error) {
      console.error('Error loading ficha:', error);
    } finally {
      setLoadingFicha(false);
    }
  };
  
  const loadCalificaciones = async () => {
    if (!claseId) return;
    try {
      const response = await axios.get(
        `${API}/profesor/estudiante/${estudianteId}/calificaciones-completas?clase_id=${claseId}`,
        { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }}
      );
      setCalificacionesCompletas(response.data);
    } catch (error) {
      console.error('Error loading calificaciones:', error);
    }
  };
  
  const abrirModalAgregar = () => {
    setCalificacionEditando(null);
    setFormCalificacion({ nombre: '', calificacion: '', descripcion: '' });
    setModalCalificacionOpen(true);
  };
  
  const abrirModalEditar = (calificacion) => {
    setCalificacionEditando(calificacion);
    setFormCalificacion({
      nombre: calificacion.nombre,
      calificacion: calificacion.calificacion.toString(),
      descripcion: calificacion.descripcion || ''
    });
    setModalCalificacionOpen(true);
  };
  
  const guardarCalificacion = async () => {
    if (!formCalificacion.nombre.trim()) {
      toast.error('El nombre es requerido');
      return;
    }
    const calificacionNum = parseFloat(formCalificacion.calificacion);
    if (isNaN(calificacionNum) || calificacionNum < 0 || calificacionNum > 100) {
      toast.error('La calificación debe ser un número entre 0 y 100');
      return;
    }
    setSavingCalificacion(true);
    try {
      const headers = { 'Authorization': `Bearer ${localStorage.getItem('token')}` };
      if (calificacionEditando) {
        await axios.put(`${API}/profesor/calificacion-extra/${calificacionEditando.id}`, {
          nombre: formCalificacion.nombre,
          calificacion: calificacionNum,
          descripcion: formCalificacion.descripcion
        }, { headers });
        toast.success('Calificación actualizada');
      } else {
        await axios.post(`${API}/profesor/calificacion-extra`, {
          estudiante_id: estudianteId,
          clase_id: claseId,
          nombre: formCalificacion.nombre,
          calificacion: calificacionNum,
          descripcion: formCalificacion.descripcion
        }, { headers });
        toast.success('Calificación agregada');
      }
      setModalCalificacionOpen(false);
      loadCalificaciones();
    } catch (error) {
      console.error('Error saving calificacion:', error);
      toast.error('Error al guardar calificación');
    } finally {
      setSavingCalificacion(false);
    }
  };
  
  const eliminarCalificacion = async (calificacionId) => {
    if (!window.confirm('¿Estás seguro de eliminar esta calificación?')) return;
    setDeletingCalificacion(calificacionId);
    try {
      await axios.delete(`${API}/profesor/calificacion-extra/${calificacionId}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      toast.success('Calificación eliminada');
      loadCalificaciones();
    } catch (error) {
      console.error('Error deleting calificacion:', error);
      toast.error('Error al eliminar calificación');
    } finally {
      setDeletingCalificacion(null);
    }
  };

  const guardarNotaPrivada = async () => {
    if (!claseId) return;
    setSavingNota(true);
    try {
      await axios.post(`${API}/profesor/notas-privadas`, {
        estudiante_id: estudianteId,
        clase_id: claseId,
        nota: notaPrivada
      }, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      setNotaOriginal(notaPrivada);
      toast.success('Nota guardada correctamente');
    } catch (error) {
      console.error('Error saving nota:', error);
      toast.error('Error al guardar la nota');
    } finally {
      setSavingNota(false);
    }
  };

  const enviarAlertaCalificacion = async () => {
    if (!detalleClase || !claseId) return;
    setSendingAlert(true);
    try {
      await axios.post(`${API}/profesor/enviar-alerta-calificacion`, {
        estudiante_id: estudianteId,
        clase_id: claseId,
        promedio: detalleClase.promedio_clase
      }, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      toast.success(`Email de alerta enviado a ${estudiante?.nombre_completo}`);
    } catch (error) {
      console.error('Error sending alert:', error);
      toast.error('Error al enviar email de alerta');
    } finally {
      setSendingAlert(false);
    }
  };
  
  const exportarFichaPDF = async () => {
    if (!claseId || !estudianteId) {
      toast.error('Selecciona una clase para exportar la ficha');
      return;
    }
    setExportandoPDF(true);
    try {
      const response = await axios.get(
        `${API}/profesor/estudiante/${estudianteId}/ficha-pdf?clase_id=${claseId}`,
        {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
          responseType: 'blob'
        }
      );
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      let fileName = `ficha_${estudiante?.nombre_completo?.replace(/\s+/g, '_') || 'estudiante'}.pdf`;
      const contentDisposition = response.headers['content-disposition'];
      if (contentDisposition) {
        const fileNameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (fileNameMatch && fileNameMatch[1]) {
          fileName = fileNameMatch[1].replace(/['"]/g, '');
        }
      }
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Ficha PDF descargada correctamente');
    } catch (error) {
      console.error('Error exporting PDF:', error);
      toast.error('Error al exportar la ficha a PDF');
    } finally {
      setExportandoPDF(false);
    }
  };

  const getEstadoConfig = (estado) => {
    switch (estado) {
      case 'excelente':
        return { color: 'bg-green-500', textColor: 'text-green-700', bgLight: 'bg-green-50', icon: Award, label: 'Excelente' };
      case 'regular':
        return { color: 'bg-yellow-500', textColor: 'text-yellow-700', bgLight: 'bg-yellow-50', icon: Target, label: 'Regular' };
      case 'en_riesgo':
        return { color: 'bg-red-500', textColor: 'text-red-700', bgLight: 'bg-red-50', icon: AlertTriangle, label: 'En Riesgo' };
      default:
        return { color: 'bg-gray-400', textColor: 'text-gray-600', bgLight: 'bg-gray-50', icon: BookOpen, label: 'Sin datos' };
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!estudiante) {
    return (
      <div className="text-center py-12">
        <User className="w-16 h-16 mx-auto text-gray-300 mb-4" />
        <h2 className="text-xl font-semibold text-gray-700">Estudiante no encontrado</h2>
        <Button variant="outline" onClick={handleBack} className="mt-4">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Volver
        </Button>
      </div>
    );
  }

  const claseActual = claseId ? rendimientoGlobal?.clases?.find(c => c.clase_id === claseId) : null;

  return (
    <div className="space-y-6 max-w-7xl mx-auto">
      {/* Back Button */}
      <Button 
        variant="ghost" 
        onClick={handleBack}
        className="text-gray-600 hover:text-gray-800"
        data-testid="back-button"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Volver al Panel de Avance
      </Button>

      {/* Student Header */}
      <Card className="border-0 shadow-xl bg-gradient-to-br from-blue-600 to-indigo-700 text-white overflow-hidden">
        <CardContent className="p-6 md:p-8">
          <div className="flex flex-col md:flex-row items-center gap-6">
            <div className="relative">
              <div className="w-28 h-28 md:w-36 md:h-36 rounded-full overflow-hidden bg-white/20 border-4 border-white/30 shadow-lg">
                {estudiante.foto ? (
                  <img src={getImageUrl ? getImageUrl(estudiante.foto) : estudiante.foto} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <User className="w-16 h-16 text-white/70" />
                  </div>
                )}
              </div>
              {detalleClase && (
                <div className={`absolute -bottom-2 -right-2 w-12 h-12 rounded-full flex items-center justify-center text-white font-bold shadow-lg ${
                  detalleClase.promedio_clase >= 80 ? 'bg-green-500' :
                  detalleClase.promedio_clase >= 60 ? 'bg-yellow-500' : 'bg-red-500'
                }`}>
                  {detalleClase.promedio_clase}%
                </div>
              )}
            </div>
            
            <div className="flex-1 text-center md:text-left">
              <h1 className="text-2xl md:text-3xl font-bold mb-2">{estudiante.nombre_completo}</h1>
              <div className="flex flex-wrap justify-center md:justify-start gap-4 text-blue-100">
                <span className="flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  {estudiante.email}
                </span>
                {estudiante.celular && (
                  <span className="flex items-center gap-2">
                    <Phone className="w-4 h-4" />
                    {estudiante.celular}
                  </span>
                )}
                {estudiante.iglesia && (
                  <span className="flex items-center gap-2">
                    <Church className="w-4 h-4" />
                    {estudiante.iglesia}
                  </span>
                )}
              </div>
              
              {rendimientoGlobal?.resumen && (
                <div className="flex flex-wrap justify-center md:justify-start gap-3 mt-4">
                  <Badge className="bg-white/20 text-white border-white/30">
                    <BookOpen className="w-3 h-3 mr-1" />
                    {rendimientoGlobal.resumen.total_clases} clases totales
                  </Badge>
                  <Badge className="bg-green-500/80 text-white">
                    {rendimientoGlobal.resumen.clases_excelentes} excelentes
                  </Badge>
                  <Badge className="bg-yellow-500/80 text-white">
                    {rendimientoGlobal.resumen.clases_regulares} regulares
                  </Badge>
                  {rendimientoGlobal.resumen.clases_en_riesgo > 0 && (
                    <Badge className="bg-red-500/80 text-white">
                      {rendimientoGlobal.resumen.clases_en_riesgo} en riesgo
                    </Badge>
                  )}
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Main Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        
        {/* Left Column: Class Detail */}
        <div className="lg:col-span-2 space-y-6">
          
          {detalleClase && claseActual && (
            <Card className="shadow-lg border-l-4 border-blue-500">
              <CardHeader className="bg-gradient-to-r from-blue-50 to-indigo-50">
                <CardTitle className="flex items-center justify-between flex-wrap gap-2">
                  <span className="flex items-center gap-2 text-blue-800">
                    <GraduationCap className="w-5 h-5" />
                    Rendimiento en: {claseActual.titulo}
                    {esSoloLectura && (
                      <Badge variant="outline" className="ml-2 text-xs text-gray-500 border-gray-300">
                        Solo lectura
                      </Badge>
                    )}
                  </span>
                  <div className="flex gap-2">
                    <Button 
                      size="sm"
                      variant="outline"
                      onClick={exportarFichaPDF}
                      disabled={exportandoPDF}
                      className="text-blue-600 border-blue-300 hover:bg-blue-50"
                      data-testid="export-pdf-btn"
                    >
                      {exportandoPDF ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                      ) : (
                        <Download className="w-4 h-4 mr-2" />
                      )}
                      Exportar PDF
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setEnviarFichaForm((f) => ({
                          ...f,
                          destinatario_email: estudiante?.email || '',
                          cc_input: '',
                          mensaje_personal: '',
                        }));
                        setShowEnviarFichaModal(true);
                      }}
                      className="text-emerald-700 border-emerald-300 hover:bg-emerald-50"
                      data-testid="enviar-ficha-email-btn"
                    >
                      <Mail className="w-4 h-4 mr-2" />
                      Enviar Ficha por Email
                    </Button>
                    {!esSoloLectura && detalleClase.promedio_clase < 70 && (
                      <Button 
                        size="sm"
                        variant="destructive"
                        onClick={enviarAlertaCalificacion}
                        disabled={sendingAlert}
                        data-testid="send-alert-btn"
                      >
                        {sendingAlert ? (
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        ) : (
                          <Send className="w-4 h-4 mr-2" />
                        )}
                        Enviar Alerta
                      </Button>
                    )}
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6 space-y-6">
                {/* 4-column stats grid with Videos Vistos */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="stats-grid">
                  <div className="text-center p-4 bg-blue-50 rounded-xl border border-blue-100">
                    <BarChart3 className="w-5 h-5 mx-auto text-blue-500 mb-1" />
                    <p className="text-xs text-blue-600 font-medium">Promedio</p>
                    <p className={`text-2xl font-bold ${
                      detalleClase.promedio_clase >= 80 ? 'text-green-600' :
                      detalleClase.promedio_clase >= 60 ? 'text-yellow-600' : 'text-red-600'
                    }`}>
                      {detalleClase.promedio_clase}%
                    </p>
                  </div>
                  <div className="text-center p-4 bg-green-50 rounded-xl border border-green-100">
                    <Calendar className="w-5 h-5 mx-auto text-green-500 mb-1" />
                    <p className="text-xs text-green-600 font-medium">Asistencia</p>
                    <p className="text-2xl font-bold text-green-600">
                      {detalleClase.total_asistencias}/15
                    </p>
                    <p className="text-xs text-green-500">{detalleClase.porcentaje_asistencia}%</p>
                  </div>
                  <div className="text-center p-4 bg-purple-50 rounded-xl border border-purple-100">
                    <Video className="w-5 h-5 mx-auto text-purple-500 mb-1" />
                    <p className="text-xs text-purple-600 font-medium">Videos Vistos</p>
                    <p className="text-2xl font-bold text-purple-600">
                      {detalleClase.video_accesos ?? 0}/{detalleClase.total_videos_clase ?? 0}
                    </p>
                    <p className="text-xs text-purple-500">
                      {detalleClase.total_videos_clase > 0
                        ? `${Math.round((detalleClase.video_accesos / detalleClase.total_videos_clase) * 100)}%`
                        : 'Sin videos'}
                    </p>
                  </div>
                  <div className="text-center p-4 bg-amber-50 rounded-xl border border-amber-100">
                    <CheckCircle className="w-5 h-5 mx-auto text-amber-500 mb-1" />
                    <p className="text-xs text-amber-600 font-medium">Evaluaciones</p>
                    <p className="text-2xl font-bold text-amber-600">
                      {calificacionesCompletas?.resumen?.total_calificaciones ?? 0}
                    </p>
                    <p className="text-xs text-amber-500">registradas</p>
                  </div>
                </div>
                
                {/* Grades Chart */}
                {historialCalificaciones.length > 0 && (
                  <div className="bg-gradient-to-br from-slate-50 to-blue-50 p-4 rounded-xl border border-blue-100">
                    <h4 className="font-semibold text-gray-700 flex items-center gap-2 mb-4">
                      <BarChart3 className="w-5 h-5 text-blue-600" />
                      Tendencia de Calificaciones
                    </h4>
                    <ResponsiveContainer width="100%" height={250}>
                      <LineChart data={historialCalificaciones} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis dataKey="fecha" tick={{ fontSize: 12 }} tickLine={false} />
                        <YAxis domain={[0, 100]} tick={{ fontSize: 12 }} tickLine={false} axisLine={false} />
                        <Tooltip 
                          contentStyle={{ backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '8px' }}
                          formatter={(value, name) => [`${value}%`, name === 'calificacion' ? 'Calificación' : 'Promedio']}
                        />
                        <Legend />
                        <ReferenceLine y={80} stroke="#22c55e" strokeDasharray="5 5" />
                        <ReferenceLine y={60} stroke="#eab308" strokeDasharray="5 5" />
                        <Line type="monotone" dataKey="calificacion" name="Calificación" stroke="#3b82f6" strokeWidth={2} dot={{ fill: '#3b82f6', r: 4 }} />
                        <Line type="monotone" dataKey="promedio_acumulado" name="Promedio" stroke="#8b5cf6" strokeWidth={2} strokeDasharray="5 5" dot={false} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                )}

                {/* Complete Grades Section */}
                {calificacionesCompletas && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h4 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                        <ClipboardList className="w-5 h-5 text-indigo-600" />
                        Calificaciones de la Clase
                      </h4>
                      <div className="text-right">
                        <p className="text-sm text-gray-500">Promedio Total</p>
                        <p className={`text-2xl font-bold ${
                          calificacionesCompletas.resumen.promedio_total >= 80 ? 'text-green-600' :
                          calificacionesCompletas.resumen.promedio_total >= 60 ? 'text-yellow-600' : 'text-red-600'
                        }`}>
                          {calificacionesCompletas.resumen.promedio_total}%
                        </p>
                      </div>
                    </div>
                    
                    {/* From Tests */}
                    {calificacionesCompletas.de_pruebas.items.length > 0 && (
                      <div className="bg-gradient-to-br from-purple-50 to-indigo-50 p-4 rounded-xl border border-purple-200">
                        <div className="flex items-center justify-between mb-3">
                          <h5 className="font-semibold text-purple-800 flex items-center gap-2">
                            <FileText className="w-4 h-4" />
                            De Pruebas y Exámenes ({calificacionesCompletas.de_pruebas.cantidad})
                          </h5>
                          <Badge className="bg-purple-100 text-purple-700">
                            Promedio: {calificacionesCompletas.de_pruebas.promedio}%
                          </Badge>
                        </div>
                        <div className="space-y-2">
                          {calificacionesCompletas.de_pruebas.items.map((cal, idx) => (
                            <div key={idx} className="flex items-center justify-between p-2 bg-white rounded-lg">
                              <span className="text-gray-700">{cal.nombre}</span>
                              <span className={`font-bold ${
                                cal.calificacion >= 80 ? 'text-green-600' :
                                cal.calificacion >= 60 ? 'text-yellow-600' : 'text-red-600'
                              }`}>
                                {cal.calificacion}%
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* From Homework */}
                    {calificacionesCompletas.de_tareas.items.length > 0 && (
                      <div className="bg-gradient-to-br from-blue-50 to-cyan-50 p-4 rounded-xl border border-blue-200">
                        <div className="flex items-center justify-between mb-3">
                          <h5 className="font-semibold text-blue-800 flex items-center gap-2">
                            <BookOpen className="w-4 h-4" />
                            De Tareas ({calificacionesCompletas.de_tareas.cantidad})
                          </h5>
                          <Badge className="bg-blue-100 text-blue-700">
                            Promedio: {calificacionesCompletas.de_tareas.promedio}%
                          </Badge>
                        </div>
                        <div className="space-y-2">
                          {calificacionesCompletas.de_tareas.items.map((cal, idx) => (
                            <div key={idx} className="flex items-center justify-between p-2 bg-white rounded-lg">
                              <span className="text-gray-700">{cal.nombre}</span>
                              <span className={`font-bold ${
                                cal.calificacion >= 80 ? 'text-green-600' :
                                cal.calificacion >= 60 ? 'text-yellow-600' : 'text-red-600'
                              }`}>
                                {cal.calificacion}%
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Extra Grades - EDITABLE */}
                    <div className="bg-gradient-to-br from-emerald-50 to-teal-50 p-4 rounded-xl border border-emerald-200">
                      <div className="flex items-center justify-between mb-3">
                        <h5 className="font-semibold text-emerald-800 flex items-center gap-2">
                          <Star className="w-4 h-4" />
                          Calificaciones Extras ({calificacionesCompletas.extras.cantidad})
                        </h5>
                        <div className="flex items-center gap-2">
                          {calificacionesCompletas.extras.cantidad > 0 && (
                            <Badge className="bg-emerald-100 text-emerald-700">
                              Promedio: {calificacionesCompletas.extras.promedio}%
                            </Badge>
                          )}
                          {!esSoloLectura && (
                            <Button 
                              size="sm" 
                              onClick={abrirModalAgregar}
                              className="bg-emerald-600 hover:bg-emerald-700"
                              data-testid="add-extra-grade-btn"
                            >
                              <Plus className="w-4 h-4 mr-1" />
                              Agregar
                            </Button>
                          )}
                        </div>
                      </div>
                      
                      {calificacionesCompletas.extras.items.length > 0 ? (
                        <div className="space-y-2">
                          {calificacionesCompletas.extras.items.map((cal) => (
                            <div key={cal.id} className="flex items-center justify-between p-3 bg-white rounded-lg group hover:shadow-md transition-all">
                              <div className="flex-1">
                                <p className="font-medium text-gray-800">{cal.nombre}</p>
                                {cal.descripcion && (
                                  <p className="text-xs text-gray-500">{cal.descripcion}</p>
                                )}
                              </div>
                              <div className="flex items-center gap-2">
                                <span className={`font-bold text-lg px-3 py-1 rounded-full ${
                                  cal.calificacion >= 80 ? 'bg-green-100 text-green-700' :
                                  cal.calificacion >= 60 ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'
                                }`}>
                                  {cal.calificacion}%
                                </span>
                                {!esSoloLectura && (
                                  <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                    <Button 
                                      size="icon" 
                                      variant="ghost"
                                      className="h-8 w-8 text-blue-600 hover:bg-blue-100"
                                      onClick={() => abrirModalEditar(cal)}
                                    >
                                      <Pencil className="w-4 h-4" />
                                    </Button>
                                    <Button 
                                      size="icon" 
                                      variant="ghost"
                                      className="h-8 w-8 text-red-600 hover:bg-red-100"
                                      onClick={() => eliminarCalificacion(cal.id)}
                                      disabled={deletingCalificacion === cal.id}
                                    >
                                      {deletingCalificacion === cal.id ? (
                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600"></div>
                                      ) : (
                                        <Trash2 className="w-4 h-4" />
                                      )}
                                    </Button>
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-6 text-gray-500">
                          <Star className="w-10 h-10 mx-auto text-gray-300 mb-2" />
                          <p>No hay calificaciones extras</p>
                          {!esSoloLectura && (
                            <p className="text-xs">Agrega participación, exposiciones, trabajos voluntarios, etc.</p>
                          )}
                        </div>
                      )}
                    </div>
                    
                    {calificacionesCompletas.resumen.total_calificaciones === 0 && (
                      <div className="text-center py-8 bg-gray-50 rounded-xl">
                        <ClipboardList className="w-12 h-12 mx-auto text-gray-300 mb-2" />
                        <p className="text-gray-500">No hay calificaciones registradas</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Attendance Detail */}
                {detalleClase.asistencias?.length > 0 && (
                  <div data-testid="attendance-detail">
                    <h4 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                      <Calendar className="w-5 h-5 text-blue-600" />
                      Registro de Asistencias
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {[...Array(detalleClase.total_lecciones || detalleClase.asistencias.length || 15)].map((_, idx) => {
                        const asist = detalleClase.asistencias.find(a => a.leccion_numero === idx + 1);
                        const tipo = asist?.tipo;
                        const presente = asist?.presente;
                        // Verde = presente o vio video (ambos cuentan como asistencia)
                        // Rojo = ausente | Gris = sin registro
                        let cls = 'bg-gray-100 text-gray-400 border-2 border-gray-200';
                        let title = `Lección ${idx + 1}`;
                        if (tipo === 'presente' || (tipo === undefined && presente === true)) {
                          cls = 'bg-green-100 text-green-700 border-2 border-green-300';
                          title += ' · Presente';
                          if (asist?.fecha) title += ` (${asist.fecha})`;
                        } else if (tipo === 'video') {
                          // Cuenta como presente, pero diferenciamos con un pequeño indicador
                          cls = 'bg-green-100 text-green-700 border-2 border-green-300 relative';
                          title += ' · Presente (vio el video)';
                          if (asist?.fecha) title += ` (${asist.fecha})`;
                        } else if (tipo === 'ausente' || presente === false) {
                          cls = 'bg-red-100 text-red-700 border-2 border-red-300';
                          title += ' · Ausente';
                          if (asist?.fecha) title += ` (${asist.fecha})`;
                        }
                        const esVideo = tipo === 'video';
                        return (
                          <div
                            key={idx}
                            className={`w-10 h-10 rounded-lg flex items-center justify-center text-sm font-medium transition-all relative ${cls}`}
                            title={title}
                          >
                            {idx + 1}
                            {esVideo && (
                              <span
                                className="absolute -top-1 -right-1 w-3.5 h-3.5 bg-blue-500 rounded-full flex items-center justify-center text-white text-[8px] font-bold border border-white"
                                title="Asistencia por video"
                              >
                                ▶
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    <div className="flex items-center gap-4 mt-2 text-xs text-gray-500 flex-wrap">
                      <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-green-200 border border-green-300 inline-block"></span> Presente</span>
                      <span className="flex items-center gap-1">
                        <span className="w-3 h-3 rounded bg-green-200 border border-green-300 inline-block relative">
                          <span className="absolute -top-1 -right-1 w-2 h-2 bg-blue-500 rounded-full"></span>
                        </span>
                        Presente (vio video)
                      </span>
                      <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-red-200 border border-red-300 inline-block"></span> Ausente</span>
                      <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-gray-200 border border-gray-300 inline-block"></span> Sin registro</span>
                    </div>
                  </div>
                )}

                {/* Shared Teacher Notes */}
                {claseId && (
                  <div className="bg-gradient-to-br from-sky-50 to-blue-50 p-4 rounded-xl border border-sky-200" data-testid="shared-notes-section">
                    <div className="flex items-center gap-2 mb-3">
                      <Users className="w-5 h-5 text-sky-600" />
                      <h4 className="font-semibold text-gray-700">Notas para Profesores</h4>
                      <Badge variant="outline" className="text-xs text-sky-600 border-sky-300">
                        Compartidas
                      </Badge>
                    </div>
                    
                    {/* Add new note */}
                    <div className="flex gap-2 mb-3">
                      <Textarea
                        placeholder="Escribe una nota visible para todos los profesores y administradores..."
                        value={nuevaNotaCompartida}
                        onChange={(e) => setNuevaNotaCompartida(e.target.value)}
                        className="min-h-[60px] bg-white border-sky-200 focus:border-sky-400 flex-1"
                        data-testid="shared-note-input"
                      />
                      <Button
                        size="sm"
                        onClick={guardarNotaCompartida}
                        disabled={savingNotaCompartida || !nuevaNotaCompartida.trim()}
                        className="bg-sky-600 hover:bg-sky-700 self-end"
                        data-testid="save-shared-note-btn"
                      >
                        {savingNotaCompartida ? (
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        ) : (
                          <MessageSquare className="w-4 h-4" />
                        )}
                      </Button>
                    </div>

                    {/* List of shared notes */}
                    {loadingNotasCompartidas ? (
                      <div className="flex justify-center py-4">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-sky-600"></div>
                      </div>
                    ) : notasCompartidas.length > 0 ? (
                      <div className="space-y-2 max-h-[300px] overflow-y-auto">
                        {notasCompartidas.map((nota) => (
                          <div key={nota.id} className="bg-white p-3 rounded-lg border border-sky-100 group" data-testid={`shared-note-${nota.id}`}>
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <span className="text-xs font-semibold text-sky-700">{nota.profesor_nombre}</span>
                                  <span className="text-xs text-gray-400">
                                    {nota.fecha_creacion ? new Date(nota.fecha_creacion).toLocaleDateString('es-UY', {
                                      day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
                                    }) : ''}
                                  </span>
                                </div>
                                <p className="text-sm text-gray-700 whitespace-pre-wrap">{nota.nota}</p>
                              </div>
                              {(nota.es_propia || user?.rol === 'admin') && (
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  className="h-7 w-7 text-red-400 hover:text-red-600 hover:bg-red-50 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
                                  onClick={() => eliminarNotaCompartida(nota.id)}
                                  data-testid={`delete-shared-note-${nota.id}`}
                                >
                                  <Trash2 className="w-3 h-3" />
                                </Button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-400 text-center py-2">No hay notas compartidas aún</p>
                    )}
                  </div>
                )}

                {/* Private Notes - Only for class owner */}
                {!esSoloLectura && (
                  <div className="bg-gradient-to-br from-amber-50 to-orange-50 p-4 rounded-xl border border-amber-200" data-testid="private-notes-section">
                    <div className="flex items-center gap-2 mb-3">
                      <StickyNote className="w-5 h-5 text-amber-600" />
                      <h4 className="font-semibold text-gray-700">Notas Privadas del Profesor</h4>
                      <Badge variant="outline" className="text-xs text-amber-600 border-amber-300">
                        Solo visible para ti
                      </Badge>
                    </div>
                    <Textarea
                      placeholder="Escribe observaciones, recordatorios o notas sobre este estudiante..."
                      value={notaPrivada}
                      onChange={(e) => setNotaPrivada(e.target.value)}
                      className="min-h-[100px] bg-white border-amber-200 focus:border-amber-400"
                      data-testid="private-note-input"
                    />
                    <div className="flex justify-end mt-3">
                      <Button 
                        size="sm"
                        onClick={guardarNotaPrivada}
                        disabled={savingNota || notaPrivada === notaOriginal}
                        className="bg-amber-600 hover:bg-amber-700"
                        data-testid="save-private-note-btn"
                      >
                        {savingNota ? (
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        ) : (
                          <Save className="w-4 h-4 mr-2" />
                        )}
                        Guardar Nota
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
          
          {!claseId && (
            <Card className="shadow-lg">
              <CardContent className="p-8 text-center">
                <BookOpen className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                <h3 className="text-xl font-semibold text-gray-700 mb-2">Selecciona una clase</h3>
                <p className="text-gray-500">Haz clic en una de las clases de la lista para ver el detalle del rendimiento</p>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Right Column: Other Classes */}
        <div className="space-y-6">
          <Card className="shadow-lg border-t-4 border-indigo-500">
            <CardHeader className="bg-gradient-to-r from-indigo-50 to-purple-50">
              <CardTitle className="flex items-center gap-2 text-indigo-800">
                <TrendingUp className="w-5 h-5" />
                Rendimiento en Otras Clases
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4">
              {rendimientoGlobal?.clases?.length > 0 ? (
                <div className="space-y-3">
                  {rendimientoGlobal.clases.map((clase) => {
                    const estadoConfig = getEstadoConfig(clase.estado);
                    const esClaseActual = clase.clase_id === claseId;
                    const IconEstado = estadoConfig.icon;
                    
                    return (
                      <div 
                        key={clase.clase_id}
                        onClick={() => navigate(`/app/estudiante/${estudianteId}?clase_id=${clase.clase_id}`)}
                        className={`p-3 rounded-xl cursor-pointer transition-all border-2 ${
                          esClaseActual 
                            ? 'border-blue-500 bg-blue-50 shadow-md' 
                            : 'border-transparent hover:border-gray-200 hover:bg-gray-50'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${estadoConfig.color}`}>
                            <span className="text-white font-bold text-sm">
                              {clase.promedio}%
                            </span>
                          </div>
                          
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <p className="font-medium text-gray-800 truncate">{clase.titulo}</p>
                              {esClaseActual && (
                                <Badge className="bg-blue-500 text-white text-xs">Actual</Badge>
                              )}
                              {!clase.activa && (
                                <Badge variant="outline" className="text-xs text-gray-500">Inactiva</Badge>
                              )}
                            </div>
                            <div className="flex items-center gap-2 mt-1">
                              <Badge variant="outline" className={`text-xs ${estadoConfig.textColor} border-current`}>
                                <IconEstado className="w-3 h-3 mr-1" />
                                {estadoConfig.label}
                              </Badge>
                              <span className="text-xs text-gray-500">
                                {clase.total_evaluaciones} eval. | {clase.asistencias} asist.
                              </span>
                            </div>
                          </div>
                        </div>
                        
                        <div className="mt-2">
                          <Progress 
                            value={clase.promedio} 
                            className="h-1.5"
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8">
                  <BookOpen className="w-12 h-12 mx-auto text-gray-300 mb-2" />
                  <p className="text-gray-500 text-sm">No hay otras clases registradas</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* General Average */}
          {rendimientoGlobal?.resumen && (
            <Card className={`shadow-lg border-t-4 ${
              rendimientoGlobal.resumen.promedio_general >= 80 ? 'border-green-500' :
              rendimientoGlobal.resumen.promedio_general >= 60 ? 'border-yellow-500' : 'border-red-500'
            }`}>
              <CardContent className="p-6 text-center">
                <p className="text-sm text-gray-600 font-medium mb-2">Promedio General</p>
                <p className={`text-5xl font-bold ${
                  rendimientoGlobal.resumen.promedio_general >= 80 ? 'text-green-600' :
                  rendimientoGlobal.resumen.promedio_general >= 60 ? 'text-yellow-600' : 'text-red-600'
                }`}>
                  {rendimientoGlobal.resumen.promedio_general}%
                </p>
                <p className="text-xs text-gray-500 mt-2">
                  Calculado de {rendimientoGlobal.resumen.total_clases} clases
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
      
      {/* Student Registration Card - Redesigned Accordion */}
      {(user?.rol === 'admin' || user?.rol === 'profesor') && (
        <Card className="shadow-lg border-0 overflow-hidden" data-testid="ficha-registro-section">
          <Accordion type="single" collapsible onValueChange={(val) => {
            if (val === 'ficha' && !fichaRegistro) loadFichaRegistro();
          }}>
            <AccordionItem value="ficha" className="border-0">
              <AccordionTrigger className="px-6 py-4 bg-gradient-to-r from-indigo-600 to-violet-600 text-white hover:from-indigo-700 hover:to-violet-700 hover:no-underline transition-all [&>svg]:text-white" data-testid="ficha-registro-toggle">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
                    <ClipboardList className="w-5 h-5 text-white" />
                  </div>
                  <div className="text-left">
                    <p className="font-bold text-base">Ficha de Registro del Estudiante</p>
                    <p className="text-xs text-indigo-200 font-normal">Datos del formulario de inscripción</p>
                  </div>
                </div>
              </AccordionTrigger>
              <AccordionContent className="px-6 pb-6 pt-4 bg-gradient-to-b from-indigo-50/50 to-white">
                {loadingFicha ? (
                  <div className="flex justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                  </div>
                ) : !fichaRegistro || fichaRegistro.respuestas.length === 0 ? (
                  <div className="text-center py-8">
                    <ClipboardList className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                    <p className="text-gray-500 text-sm">
                      Este estudiante no ha completado el formulario de registro.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <p className="text-sm text-gray-500 flex items-center gap-1">
                      <FileText className="w-4 h-4" />
                      {fichaRegistro.respuestas.length} formulario{fichaRegistro.respuestas.length > 1 ? 's' : ''} encontrado{fichaRegistro.respuestas.length > 1 ? 's' : ''}
                    </p>
                    {fichaRegistro.respuestas.map((resp, idx) => {
                      const tieneResp = resp.tiene_respuestas;
                      return (
                        <Card key={resp.id || idx} className={`overflow-hidden ${tieneResp ? 'border-indigo-200' : 'border-gray-200'}`}>
                          <Accordion type="single" collapsible>
                            <AccordionItem value={`form-${idx}`} className="border-0">
                              <AccordionTrigger className={`px-4 py-3 hover:no-underline ${tieneResp ? 'bg-indigo-50/70 hover:bg-indigo-100/70' : 'bg-gray-50 hover:bg-gray-100'}`}>
                                <div className="flex items-center gap-2">
                                  <FileText className={`w-4 h-4 ${tieneResp ? 'text-indigo-600' : 'text-gray-400'}`} />
                                  <span className={`text-sm font-semibold ${tieneResp ? 'text-indigo-700' : 'text-gray-500'}`}>
                                    Formulario {fichaRegistro.respuestas.length - idx}
                                    {!tieneResp && ' (sin respuestas)'}
                                  </span>
                                  <span className="text-xs text-gray-400 ml-auto mr-2">
                                    {resp.fecha_envio ? new Date(resp.fecha_envio).toLocaleDateString('es-UY', {
                                      day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
                                    }) : ''}
                                  </span>
                                </div>
                              </AccordionTrigger>
                              <AccordionContent className="px-0">
                                {!tieneResp ? (
                                  <p className="px-4 py-4 text-sm text-gray-400 text-center italic">
                                    Este formulario fue creado durante el registro pero no tiene respuestas completadas.
                                  </p>
                                ) : (
                                  <div className="divide-y">
                                    {resp.respuestas.filter(r => r.respuesta).map((r, rIdx) => (
                                      <div key={rIdx} className="px-4 py-3 flex flex-col sm:flex-row sm:gap-4 hover:bg-gray-50/50 transition-colors">
                                        <span className="text-sm font-medium text-gray-600 sm:w-1/3 flex-shrink-0">
                                          {(r.pregunta_titulo || r.pregunta_texto || r.pregunta || `Pregunta ${rIdx + 1}`)
                                            .replace(/<[^>]*>/g, '')
                                            .trim()}
                                        </span>
                                        <span className="text-sm text-gray-900 sm:w-2/3">
                                          {typeof r.respuesta === 'object' 
                                            ? (Array.isArray(r.respuesta) ? r.respuesta.join(', ') : JSON.stringify(r.respuesta))
                                            : (r.respuesta || '--')}
                                        </span>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </AccordionContent>
                            </AccordionItem>
                          </Accordion>
                        </Card>
                      );
                    })}
                  </div>
                )}
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </Card>
      )}

      {/* Modal for Extra Grades */}
      <Dialog open={modalCalificacionOpen} onOpenChange={setModalCalificacionOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Star className="w-5 h-5 text-emerald-600" />
              {calificacionEditando ? 'Editar Calificación Extra' : 'Agregar Calificación Extra'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">
                Nombre / Concepto *
              </label>
              <Input
                placeholder="Ej: Participación en clase, Exposición voluntaria..."
                value={formCalificacion.nombre}
                onChange={(e) => setFormCalificacion(prev => ({ ...prev, nombre: e.target.value }))}
                className="w-full"
                data-testid="extra-grade-name-input"
              />
            </div>
            
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">
                Calificación (0-100) *
              </label>
              <Input
                type="number"
                min="0"
                max="100"
                placeholder="Ej: 85"
                value={formCalificacion.calificacion}
                onChange={(e) => setFormCalificacion(prev => ({ ...prev, calificacion: e.target.value }))}
                className="w-full"
                data-testid="extra-grade-score-input"
              />
            </div>
            
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">
                Descripción (opcional)
              </label>
              <Textarea
                placeholder="Notas adicionales sobre esta calificación..."
                value={formCalificacion.descripcion}
                onChange={(e) => setFormCalificacion(prev => ({ ...prev, descripcion: e.target.value }))}
                className="w-full min-h-[80px]"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setModalCalificacionOpen(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={guardarCalificacion}
              disabled={savingCalificacion}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="save-extra-grade-btn"
            >
              {savingCalificacion ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              {calificacionEditando ? 'Actualizar' : 'Agregar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal: Enviar Ficha por Email */}
      <Dialog open={showEnviarFichaModal} onOpenChange={setShowEnviarFichaModal}>
        <DialogContent className="max-w-lg" data-testid="enviar-ficha-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-emerald-600" />
              Enviar Ficha de Avance por Email
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-xs text-emerald-900">
              📨 El email se envía desde la cuenta institucional <strong>ibaconnect</strong>.
              Las respuestas llegarán a <strong>{user?.email || 'tu email'}</strong> (Reply-To).
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700">Para (destinatario)</label>
              <Input
                type="email"
                value={enviarFichaForm.destinatario_email}
                onChange={(e) => setEnviarFichaForm((f) => ({ ...f, destinatario_email: e.target.value }))}
                placeholder="alumno@email.com"
                className="mt-1"
                data-testid="ficha-dest-email"
              />
              <p className="text-xs text-gray-500 mt-1">Pre-cargado con el email del alumno. Podés editarlo.</p>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700">CC (opcional)</label>
              <Input
                type="text"
                value={enviarFichaForm.cc_input}
                onChange={(e) => setEnviarFichaForm((f) => ({ ...f, cc_input: e.target.value }))}
                placeholder="otro@email.com, mama@email.com"
                className="mt-1"
                data-testid="ficha-cc-input"
              />
              <p className="text-xs text-gray-500 mt-1">Múltiples emails separados por coma. Vacío = sin copias.</p>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700">Mensaje personal (opcional)</label>
              <Textarea
                value={enviarFichaForm.mensaje_personal}
                onChange={(e) => setEnviarFichaForm((f) => ({ ...f, mensaje_personal: e.target.value }))}
                placeholder="Hola, te paso tu avance del semestre. Cualquier consulta avisame..."
                rows={4}
                className="mt-1"
                data-testid="ficha-mensaje"
              />
            </div>

            <div className="flex items-center gap-4 text-sm">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={enviarFichaForm.incluir_calificaciones}
                  onChange={(e) => setEnviarFichaForm((f) => ({ ...f, incluir_calificaciones: e.target.checked }))}
                  className="w-4 h-4 accent-emerald-600"
                  data-testid="ficha-chk-cal"
                />
                <span>Incluir calificaciones</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={enviarFichaForm.incluir_asistencias}
                  onChange={(e) => setEnviarFichaForm((f) => ({ ...f, incluir_asistencias: e.target.checked }))}
                  className="w-4 h-4 accent-emerald-600"
                  data-testid="ficha-chk-asist"
                />
                <span>Incluir asistencias</span>
              </label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEnviarFichaModal(false)} disabled={enviandoFicha}>
              Cancelar
            </Button>
            <Button
              onClick={async () => {
                const dest = (enviarFichaForm.destinatario_email || '').trim();
                if (!dest || !dest.includes('@')) {
                  toast.error('Email destinatario inválido');
                  return;
                }
                const cc_emails = (enviarFichaForm.cc_input || '')
                  .split(',')
                  .map((e) => e.trim())
                  .filter((e) => e && e.includes('@'));
                setEnviandoFicha(true);
                try {
                  const res = await axios.post(
                    `${API}/profesor/estudiante/${estudianteId}/enviar-ficha`,
                    {
                      clase_id: claseId,
                      destinatario_email: dest,
                      cc_emails,
                      mensaje_personal: enviarFichaForm.mensaje_personal || '',
                      incluir_calificaciones: enviarFichaForm.incluir_calificaciones,
                      incluir_asistencias: enviarFichaForm.incluir_asistencias,
                    },
                    { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
                  );
                  if (res.data?.preview_mode) {
                    toast.info('Modo preview: email NO enviado. Funcionará en producción.');
                  } else {
                    toast.success(res.data?.message || `Ficha enviada a ${dest}`);
                  }
                  setShowEnviarFichaModal(false);
                } catch (e) {
                  toast.error(e?.response?.data?.detail || 'Error enviando ficha');
                } finally {
                  setEnviandoFicha(false);
                }
              }}
              disabled={enviandoFicha || !enviarFichaForm.destinatario_email}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="ficha-enviar-btn"
            >
              {enviandoFicha ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Enviando...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Enviar Ficha
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DetalleEstudiante;
